import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';

import * as Handlebars from 'handlebars';
import { firstValueFrom } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectChatState, selectUiState, selectUser } from '../../../../store/app.selectors';
import * as Sentry from '@sentry/angular';

import { SnackBarService } from '../../../../core/services/snackbar.service';
import { BaseComponent } from '../../../shared/components/base/base.component';
import { uiInitialState } from '../../../shared/store/ui.reducer';
import { IUserProfile } from '../../../user/interfaces/IUserProfile';
import { IAttachment } from '../../interfaces/IAttachment';
import { ChannelConsent } from '../../interfaces/IChannel';
import { IMessageTemplate } from '../../interfaces/IMessageTemplate';
import { WorkspaceFeatures } from '../../interfaces/IWorkspace';
import { AttachmentsService } from '../../services/attachments.service';
import { GiphyService } from '../../services/giphy.service';
import { ILog } from '../../services/logs.service';
import { PermissionsService } from '../../services/permissions.service';
import { TextProcessorService } from '../../services/text-processor.service';
import { TranslaterService } from '../../services/translater.service';
import { sendMessage } from '../../store/chat.actions';
import { AppStateWithChat, chatInitialState } from '../../store/chat.reducer';
import { EmojiModalComponent } from '../emoji/emoji.component';

@Component({
  selector: 'ngx-channel-input',
  templateUrl: './channel-input.component.html',
  styleUrls: ['./channel-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChannelInputComponent extends BaseComponent implements OnInit {
  private store = inject(Store<AppStateWithChat>);
  public dialog = inject(MatDialog);
  private permissionsService = inject(PermissionsService);
  private textProcessorService = inject(TextProcessorService);
  private translaterService = inject(TranslaterService);
  public attachmentsService = inject(AttachmentsService);
  public giphyService = inject(GiphyService);
  private snackBarService = inject(SnackBarService);

  uploader = viewChild<ElementRef<HTMLElement>>('imgupload');
  myTextarea = viewChild<ElementRef>('myTextarea');

  ui = toSignal(this.store.select(selectUiState), { initialValue: uiInitialState });
  chat = toSignal(this.store.select(selectChatState), { initialValue: chatInitialState });
  user = toSignal(this.store.select(selectUser), { initialValue: null as IUserProfile | null });

  selectedChannel = computed(() => this.chat().selectedChannel);
  selectedWorkSpace = computed(() => this.chat().selectedWorkSpace);
  inputPhoneNumber = computed(() => this.chat().inputPhoneNumber);
  hideAttachments = computed(() => this.ui().hideAttachments);
  noHasConsent = computed(
    () =>
      this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.validateOptInOptOut]) &&
      !this.selectedChannel()?.enabled
  );
  smartReply = computed(() => this.chat().smartReplie);

  text = signal('');
  attachments = signal<IAttachment[]>([]);
  isSmartReplyOrAutoResponse: boolean;
  textareaHeight = 'auto';
  messagesTemplates: IMessageTemplate[] = [];
  private previousChannelEnabled = false;
  private blockedComposerExpanded = false;

  constructor() {
    super();
    effect(() => {
      const channel = this.selectedChannel();
      const templates = this.chat().messagesTemplates;
      if (templates.length) {
        this.messagesTemplates = templates;
      }
      if (channel) {
        if (channel.enabled && !this.previousChannelEnabled) {
          this.text.set('');
        }
        this.previousChannelEnabled = channel.enabled ?? false;
      }
      this.applyOptInOptOutTemplate();
    });

    effect((onCleanup) => {
      const blockedWhatsapp = this.noHasConsent() && this.selectedChannel()?.routing === 'WHATSAPP';
      const textareaRef = this.myTextarea();
      if (!textareaRef) {
        return;
      }
      if (!blockedWhatsapp) {
        if (!this.blockedComposerExpanded) {
          return;
        }
        this.blockedComposerExpanded = false;
        const timeoutId = setTimeout(() => {
          (textareaRef.nativeElement as HTMLTextAreaElement).style.height = 'auto';
        });
        onCleanup(() => clearTimeout(timeoutId));
        return;
      }
      const value = this.text();
      const timeoutId = setTimeout(() => {
        this.blockedComposerExpanded = true;
        this.resizeBlockedComposer(textareaRef.nativeElement as HTMLTextAreaElement, value);
      });
      onCleanup(() => clearTimeout(timeoutId));
    });

    effect(() => {
      const data = this.smartReply();
      if (data) {
        this.text.set(data);
        this.isSmartReplyOrAutoResponse = true;
      }
    });
  }

  ngOnInit() {
    // lifecycle hook kept for compatibility with parent init order
  }

  applyOptInOptOutTemplate(): void {
    if (this.noHasConsent()) {
      this.text.set('');
      const template = this.getMessageTemplate();
      if (template) {
        this.text.set(template.bodyText);
      }
    }
  }

  getMessageTemplate(): IMessageTemplate | null {
    const messagesTemplates = this.messagesTemplates?.length ? this.messagesTemplates : [];
    const selectedChannel = this.selectedChannel();
    let consentStatus = selectedChannel?.consentStatus;
    if (!selectedChannel?.messages?.length && selectedChannel?.routing === 'WHATSAPP') {
      consentStatus = ChannelConsent.STARTER;
    }
    let messageTemplate = messagesTemplates.find((e) => e.externalId === consentStatus) || null;
    if (!messageTemplate) {
      return null;
    }
    messageTemplate = { ...messageTemplate };
    const user = this.user();
    const context = {
      advisor: selectedChannel?.owner?.displayName || '',
      userPhone: user?.subjectMessageableEntity?.phoneNumber || '',
      dealer: user?.subAccountMessageableEntity?.displayName || '',
      customerName: selectedChannel?.displayName || '',
    };
    if (messageTemplate?.bodyText) {
      const template = Handlebars.compile(messageTemplate.bodyText);
      messageTemplate.bodyText = template(context);
    }
    return messageTemplate;
  }

  reactivateChat(): void {
    this.sendMessage();
  }

  async verifyMessage() {
    if (this.text()?.trim() || this.attachments().length) {
      if (!this.noHasConsent()) {
        if (
          (this.textProcessorService.verifyCanProffesionalize() && !this.isSmartReplyOrAutoResponse && this.text()) ||
          (this.translaterService.verifyCanTranslateWithLocalSetting && this.text())
        ) {
          const data: { text: string; log: ILog } = (await firstValueFrom(
            this.textProcessorService.openProcessor(this.text(), this.isSmartReplyOrAutoResponse)
          )) as { text: string; log: ILog };
          if (data?.text) {
            this.text.set(data.text);
            this.sendMessage(data.log);
          }
        } else {
          this.sendMessage();
        }
      }
    }
    const textarea = this.myTextarea()?.nativeElement as HTMLElement;
    if (textarea) {
      textarea.style.height = 'auto';
    }
  }

  sendMessage(log?: ILog) {
    const selectedChannel = this.selectedChannel();
    if (!selectedChannel) {
      return;
    }
    if (
      this.inputPhoneNumber() &&
      this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.checkInputPhoneNumber])
    ) {
      if (this.inputPhoneNumber() !== selectedChannel.phoneNumber) {
        this.snackBarService.showErrorSnackBar('Invalid phone number');
        Sentry.captureException({
          message: 'Phone number inconsistency',
          originalPhoneNumber: this.inputPhoneNumber(),
          channelPhoneNumber: selectedChannel.phoneNumber,
        });
        return;
      }
    }
    if (this.text()?.trim() || this.attachments().length) {
      if (this.attachments().length) {
        this.sendAttachments();
      } else {
        this.store.dispatch(
          sendMessage({
            message: this.text(),
            attachments: this.attachments(),
            log,
            template: !selectedChannel.enabled ? this.getMessageTemplate() : null,
          })
        );
      }

      if (selectedChannel.enabled) {
        this.text.set('');
      }
      this.isSmartReplyOrAutoResponse = false;
      this.attachments.set([]);
    }
  }

  sendAttachments(): void {
    const attachments = this.attachments();
    for (let index = 0; index < attachments.length; index++) {
      this.store.dispatch(
        sendMessage({
          message: index === 0 ? this.text() : null,
          attachments: [attachments[index]],
        })
      );
    }
  }

  onKeydown(event: Event) {
    event.preventDefault();
  }

  onPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text');
    if (!pastedText) {
      this.attachmentsService.onPaste(event, this.attachments(), () => this.syncAttachments());
      setTimeout(() => {
        this.text.set('');
      });
    }
  }

  onFilesSelected(event: Event): void {
    this.attachmentsService.onChange(event, this.attachments(), this.uploader()!, () => this.syncAttachments());
  }

  onDrop(event: DragEvent): void {
    this.attachmentsService.onDrop(event, this.attachments(), () => this.syncAttachments());
  }

  onRemoveAttachment(index: number): void {
    this.attachments.update((list) => list.filter((_, i) => i !== index));
  }

  private syncAttachments(): void {
    this.attachments.set([...this.attachments()]);
  }

  autoResizeTextArea() {
    const textarea = this.myTextarea()?.nativeElement as HTMLElement;
    if (!textarea) {
      return;
    }
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
    this.textareaHeight = textarea.style.height;
  }

  private resizeBlockedComposer(textarea: HTMLTextAreaElement, value: string) {
    textarea.style.height = 'auto';
    if (!value.trim()) {
      return;
    }
    textarea.style.height = `${textarea.scrollHeight}px`;
    this.textareaHeight = textarea.style.height;
  }

  openEmoji(): void {
    this.dialog
      .open(EmojiModalComponent, {
        panelClass: 'custom-emoji-modal',
      })
      .afterClosed()
      .subscribe((res: string) => {
        if (res) {
          this.text.update((current) => (current ? `${current}${res}` : res));
          setTimeout(() => this.autoResizeTextArea());
        }
      });
  }
}
